/* STUDENT TUTORIAL — "Your First Quarter" (DESIGN-tutorial-system.md §2).
 *
 * Six chapters ≈ 12 minutes to first submit. The copy below is the design
 * doc's final text — edit the doc first, then mirror here. Content lives in
 * data so it's translatable (DESIGN-localization.md) and editable without
 * touching components. Anchors are data-tut attributes on the screens. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Engine } from '@venturewrist/engine';
import type { TutCtx, TutSeries } from './types';

const Eng = Engine as any;

const TAB_LABELS: Record<string, string> = {
  overview: '📊 Overview',
  products: '⌚ Design',
  pricing: '💲 Pricing',
  production: '🏭 Production',
  marketing: '📣 Marketing',
};

// ── Predicates (read the staged draft / working game / signals) ─────────────

const stagedNewProduct = (c: TutCtx) => (c.draft.newProducts?.length ?? 0) > 0;

const segPicked = (c: TutCtx) =>
  stagedNewProduct(c) || !!c.draft.products?.some((p) => p.targetSeg);

/** Any active model hitting segment fit ≥ 60 in the live working game. */
const fitObserved = (c: TutCtx) => {
  const g = c.game as any;
  if (!g) return false;
  try {
    return (Eng.activeModels(g) as any[]).some((m) => {
      const gv = Eng.currentGen(m);
      const a = Eng.attrs(gv.spec, g);
      return Eng.segFit(gv.spec, a, m.targetSeg, g) >= 60;
    });
  } catch { return false; }
};

const designWorked = (c: TutCtx) => fitObserved(c) || (c.signals['design-edit'] ?? 0) >= 3;

const priceSet = (c: TutCtx) =>
  !!c.draft.products?.some((p) => p.gens?.some((gv) => gv.price != null)) ||
  !!c.draft.newProducts?.some((np) => np.price != null);

const productionSet = (c: TutCtx) =>
  !!c.draft.products?.some((p) => p.gens?.some((gv) => gv.production != null || gv.alloc)) ||
  !!c.draft.newProducts?.some((np) => np.production != null);

const mktChosen = (c: TutCtx) =>
  !!c.draft.products?.some((p) => p.mkt) || !!c.draft.newProducts?.some((np) => np.marketing);

const budgetSet = (c: TutCtx) =>
  !!c.draft.products?.some((p) => (p.mkt?.budget ?? 0) > 0) ||
  !!c.draft.newProducts?.some((np) => (np.marketing?.budget ?? 0) > 0);

const journalSaved = (c: TutCtx) => (c.signals['journal-saved'] ?? 0) > 0;

// ── Series ──────────────────────────────────────────────────────────────────

export const studentSeries: TutSeries = {
  id: 'student',
  title: 'Your First Quarter',
  tabAnchor: (screen) => 'tab-' + screen,
  tabHint: (screen) => `Open the ${TAB_LABELS[screen] ?? screen} tab to continue.`,
  finale: {
    title: 'Next steps',
    items: [
      'Open a distributor country (🌐 Distribution)',
      'Start research (🔬 R&D)',
      'Check what your rivals just did (🛰️ Intel)',
    ],
  },
  chapters: [
    {
      id: 'goal',
      title: 'The goal',
      // Informational chapter — no checklist actions.
      tasks: [],
      steps: [
        {
          id: 'welcome',
          screen: 'overview',
          title: 'Welcome to your company.',
          body:
            "You have $20M, no products, and three rivals who already ship watches. Over " +
            "{maxRounds} quarters you'll design, price, produce, and sell — and the market " +
            "will answer every choice. Let's play your first quarter together. " +
            "(~12 minutes; skip anytime — you can restart from the ? menu.)",
        },
        {
          id: 'scorecard',
          screen: 'overview',
          anchor: 'stat-composite',
          title: "How you're graded.",
          body:
            'This composite score is a balanced scorecard of eight things — profit, share, ' +
            "brand, quality, satisfaction, operations, innovation, financial health. Profit " +
            "alone won't win; neither will growth that bleeds cash.",
        },
        {
          id: 'cash',
          screen: 'overview',
          anchor: 'stat-cash',
          title: 'Your fuel gauge.',
          body:
            "Cash is survival. Run dry and you'll borrow at penalty rates. Most first-time " +
            'CEOs die of inventory, not of bad products — remember that in Chapter 4.',
        },
      ],
    },
    {
      id: 'design',
      title: 'Design your first watch',
      tasks: [
        { id: 'design-tab', label: 'Open the ⌚ Design tab', done: (c) => c.tab === 'products' },
        { id: 'design-seg', label: 'Pick a target segment', done: segPicked },
        { id: 'design-fit', label: 'Build toward a 60+ segment fit', done: designWorked },
        { id: 'design-commit', label: 'Commit a design', done: stagedNewProduct },
      ],
      steps: [
        {
          id: 'design-open',
          screen: 'products',
          anchor: 'tab-products',
          body: 'Open the ⌚ Design tab — time to build something.',
          done: (c) => c.tab === 'products',
        },
        {
          id: 'design-segment',
          screen: 'products',
          anchor: 'design-segment',
          title: "Pick who it's for.",
          body:
            "Five customer segments, five different definitions of 'good'. Athletes pay for " +
            'battery and durability; Value Seekers mostly care about price. Pick a target — ' +
            'the whole screen recalculates around them.',
          done: segPicked,
        },
        {
          id: 'design-parts',
          screen: 'products',
          anchor: 'design-components',
          title: 'Parts are trade-offs.',
          body:
            'Every component changes cost, weight, battery, and quality. Watch the ' +
            'segment-fit score as you swap parts: a titanium case thrills Style Icons and ' +
            'barely moves Value Seekers. Build until fit is 70+ for your segment.',
          done: designWorked,
        },
        {
          id: 'design-viewer',
          screen: 'products',
          anchor: 'design-viewer',
          title: "That's your product.",
          body:
            "What you see is what ships — your rivals' watches are rendered from their real " +
            "specs too. You'll find theirs in 🛰️ Intel later.",
        },
        {
          id: 'design-commit',
          screen: 'products',
          anchor: 'design-commit',
          title: 'Commit the design.',
          body: 'Name it and commit. Nothing is final until you submit the quarter.',
          done: stagedNewProduct,
        },
      ],
    },
    {
      id: 'price',
      title: 'Price it',
      tasks: [
        { id: 'price-tab', label: 'Open 💲 Pricing', done: (c) => c.tab === 'pricing' },
        { id: 'price-set', label: 'Set your launch price', done: priceSet },
      ],
      steps: [
        {
          id: 'price-open',
          screen: 'pricing',
          anchor: 'tab-pricing',
          body: 'Open 💲 Pricing.',
          done: (c) => c.tab === 'pricing',
        },
        {
          id: 'price-ref',
          screen: 'pricing',
          anchor: 'pricing-ref',
          title: "The reference price is the market's anchor.",
          body:
            "Your segment expects ≈{refPrice}. Price under it and demand swells but margin " +
            "thins; over it and you'd better have the quality and brand to justify it. The " +
            'price + quality + brand rating decides your share.',
        },
        {
          id: 'price-set',
          screen: 'pricing',
          anchor: 'pricing-price',
          title: 'Set your launch price.',
          body:
            'A first product usually launches at or slightly under reference — you have no ' +
            "brand yet; don't ask customers to pay for one.",
          done: priceSet,
        },
      ],
    },
    {
      id: 'produce',
      title: 'Produce it',
      tasks: [
        { id: 'prod-tab', label: 'Open 🏭 Production', done: (c) => c.tab === 'production' },
        { id: 'prod-set', label: 'Set a production volume', done: productionSet },
      ],
      steps: [
        {
          id: 'prod-open',
          screen: 'production',
          anchor: 'tab-production',
          body: 'Open 🏭 Production.',
          done: (c) => c.tab === 'production',
        },
        {
          id: 'prod-volume',
          screen: 'production',
          anchor: 'production-volume',
          title: 'The newsvendor problem — the oldest trap in operations.',
          body:
            'Order too little and you stock out (lost sales, angry customers). Order too much ' +
            'and cash turns into warehouse inventory. Volumes are in thousands of units; for ' +
            'a first product, modest beats bold — 40–80k is a sane start.',
          done: productionSet,
        },
        {
          id: 'prod-alloc',
          screen: 'production',
          anchor: 'production-alloc',
          title: 'Who builds it.',
          body:
            'Your contract manufacturer is reliable but not free. Later you can build your ' +
            "own plants and sign volume contracts — the 📚 Academy's 'economies of scale' " +
            'lesson says when. The ⚡ auto-allocate button is fine for now.',
        },
      ],
    },
    {
      id: 'market',
      title: 'Tell the world',
      tasks: [
        { id: 'mkt-tab', label: 'Open 📣 Marketing', done: (c) => c.tab === 'marketing' },
        { id: 'mkt-fit', label: 'Match channel + creative to your segment', done: mktChosen },
        { id: 'mkt-budget', label: 'Set an ad budget', done: budgetSet },
      ],
      steps: [
        {
          id: 'mkt-open',
          screen: 'marketing',
          anchor: 'tab-marketing',
          body: 'Open 📣 Marketing.',
          done: (c) => c.tab === 'marketing',
        },
        {
          id: 'mkt-channels',
          screen: 'marketing',
          anchor: 'marketing-channels',
          title: 'Channels have audiences.',
          body:
            'Sports sponsorship is almost pure Athletes; social skews young and budget; ' +
            'print reaches Professionals. Match channel and creative to your segment — ' +
            'mismatched spend is mostly wasted.',
          done: mktChosen,
        },
        {
          id: 'mkt-budget',
          screen: 'marketing',
          anchor: 'marketing-budget',
          title: 'Budget builds awareness, awareness feeds demand.',
          body:
            '1–2 $M is a reasonable first campaign. One thing to know: markets served by ' +
            "distributors don't see your ads — control is what you trade for their reach " +
            "(that's the Distribution screen, next quarter).",
          done: budgetSet,
        },
      ],
    },
    {
      id: 'submit',
      title: 'Face the market',
      tasks: [
        { id: 'sub-journal', label: 'Write a strategy-journal line (optional)', done: journalSaved },
        { id: 'sub-submit', label: 'Submit Q1', done: (c) => c.submitted },
      ],
      steps: [
        {
          id: 'sub-review',
          anchor: 'staged-count',
          title: 'Review your quarter.',
          body:
            "Everything you've staged is listed here. Nothing has happened yet — the market " +
            'moves only when you submit.',
        },
        {
          id: 'sub-journal',
          screen: 'overview',
          anchor: 'journal-card',
          title: 'Write one honest sentence.',
          body:
            "What's your plan and why? Your instructor reads this next to your actual " +
            'results — and future-you will thank present-you in Q5.',
          done: journalSaved,
        },
        {
          id: 'sub-go',
          anchor: 'submit-quarter',
          title: 'Submit Q1.',
          body:
            'The server resolves demand across 40 countries and 5 segments, your rivals ' +
            'move, and the news reports what happened. Go.',
          done: (c) => c.submitted,
        },
        {
          id: 'sub-results',
          anchor: 'result-modal',
          title: "Read the market's answer.",
          body:
            "Revenue, share, and what sold where. From here you're on your own — but every " +
            'screen has a ? that opens the theory behind it, and 🛰️ Intel shows what your ' +
            'rivals just did. Good luck, CEO.',
        },
      ],
    },
  ],
};
